/**
 * AL.E1 (AUD.14, 20/09/2026) — CAMADA DE TRADUÇÃO das telas de decisão (`/central`, `/chat?master=1`, `/backlog`).
 *
 * O dono decide olhando UMA frase em português ("o que aconteceu"), o projeto, o prazo/idade e duas ações; o
 * técnico (kind, ids, enums, detalhe cru do motor) existe atrás de "ver contexto completo". Regra negativa:
 * nenhum `snake_case`, id ou enum cru fora do bloco técnico. A tradução é PURA (sem estado) para ser testável e
 * reutilizada por UI, REST e MCP; ela nunca muda o que o evento É — só como se lê.
 */
export const DECISION_ORIGIN_LABEL = Object.freeze({
    terminal: 'terminal do projeto',
    motor: 'motor autônomo',
    dono: 'você',
    juiz: 'juiz automático',
    master: 'MASTER operacional',
    sistema: 'sistema',
});
export const DECISION_TITLE_MAX = 90;
/** `foo_bar_baz` → `foo bar baz` (só tokens minúsculos com underscore: ids/enums do motor). */
export function humanizeTokens(text) {
    return text.replace(/\b[a-z0-9]+(?:_[a-z0-9]+)+\b/g, (token) => token.replace(/_/g, ' '));
}
/** Primeira frase (ou linha) do texto, sem residuo de TUI, cortada com reticências no limite. */
export function firstSentence(text, max = DECISION_TITLE_MAX) {
    const single = text.replace(/\s+/g, ' ').trim();
    const cut = single.split(/(?<=[.!?])\s|\s—\s|\s·\s/)[0] ?? single;
    const base = cut.length > 0 ? cut : single;
    if (base.length <= max)
        return base;
    const clipped = base.slice(0, max - 1);
    const lastSpace = clipped.lastIndexOf(' ');
    return `${(lastSpace > max * 0.6 ? clipped.slice(0, lastSpace) : clipped).trimEnd()}…`;
}
export function ageLabel(ageMs) {
    if (ageMs === null || !Number.isFinite(ageMs) || ageMs < 0)
        return 'agora';
    const min = Math.round(ageMs / 60_000);
    if (min < 1)
        return 'agora';
    if (min < 60)
        return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 48)
        return `há ${h} h${min % 60 >= 30 && h < 6 ? ' e meia' : ''}`;
    return `há ${Math.floor(h / 24)} dias`;
}
export function urgencyForAge(ageMs, thresholds = { soonMs: 4 * 3_600_000, lateMs: 24 * 3_600_000 }) {
    if (ageMs === null)
        return 'normal';
    if (ageMs >= thresholds.lateMs)
        return 'late';
    if (ageMs >= thresholds.soonMs)
        return 'soon';
    return 'normal';
}
function basename(path) {
    if (!path)
        return null;
    const parts = path.replace(/[/\\]+$/, '').split(/[/\\]/);
    return parts[parts.length - 1] || null;
}
const IMPACT_LABEL = {
    blocking: 'terminal parado esperando a resposta',
    authorization_required: 'autorização pedida',
    informative: 'sem terminal parado',
    unknown: 'impacto não verificado',
};
export function describePendingQuestion(q, nowMs = Date.now()) {
    const origin = q.fromMaster ? 'master' : q.origin === 'needs_human' ? 'terminal' : q.origin === 'proposta' ? 'motor' : 'juiz';
    const ageMs = typeof q.askedAtMs === 'number' ? Math.max(0, nowMs - q.askedAtMs) : null;
    const project = q.subjectLabel || basename(q.subjectCwd) || basename(q.cwd) || null;
    const title = humanizeTokens(firstSentence(q.question));
    const actions = q.origin === 'proposta'
        ? [{ id: 'authorize', label: 'Autorizar' }, { id: 'decline', label: 'Recusar' }]
        : [{ id: 'answer', label: 'Responder' }, { id: 'dismiss', label: 'Dispensar' }];
    const urgency = q.impact === 'blocking' ? urgencyForAge(ageMs, { soonMs: 30 * 60_000, lateMs: 4 * 3_600_000 }) : urgencyForAge(ageMs);
    const whenLabel = `${ageLabel(ageMs)}${q.impact ? ` · ${IMPACT_LABEL[q.impact]}` : ''}`;
    const technical = [q.origin ? `origem: ${q.origin}` : null, q.impact ? `impacto: ${q.impact}` : null, q.subjectCwd ?? q.cwd ? `cwd: ${q.subjectCwd ?? q.cwd}` : null, q.question, q.rationale ? `racional: ${q.rationale}` : null]
        .filter((line) => !!line).join('\n');
    return { title, project, whenLabel, ageMs, urgency, origin, originLabel: DECISION_ORIGIN_LABEL[origin], actions, technical };
}
const OPEN_PROJECT = (_project) => ({ id: 'open_project', label: 'Abrir projeto', href: '/deck' });
const HEALTH = { id: 'health', label: 'Ver saúde do sistema', href: '/observability' };
const EVIDENCE = { id: 'evidence', label: 'Abrir evidências', href: '/evidence' };
const APPROVALS = { id: 'approvals', label: 'Abrir aprovações', href: '/approvals' };
const CENTRAL = { id: 'central', label: 'Responder na Atenção', href: '/central' };
const CONVERSA = { id: 'conversa', label: 'Abrir a Conversa', href: '/chat' };
const VERSION_RE = /\b(\d+\.\d+\.\d+)\b/;
const KIND_COPY = Object.freeze({
    version_delivered: { title: (p, d) => { const v = d?.match(VERSION_RE)?.[1]; return `${v ? `Versão ${v}` : 'Uma versão'} foi entregue em ${p}`; }, origin: 'motor', action: HEALTH },
    pending_restart: { title: (_p, d) => { const v = d?.match(VERSION_RE)?.[1]; return `${v ? `Versão ${v}` : 'Uma versão'} pronta, aguardando o reinício do servidor`; }, origin: 'motor', action: HEALTH },
    fleet_idle: { title: () => 'Frota ociosa com trabalho pendente no backlog', origin: 'motor', action: { id: 'backlog', label: 'Ver o backlog', href: '/backlog' } },
    anomaly_critical: { title: (p) => `Anomalia crítica em ${p}`, origin: 'sistema', action: HEALTH },
    pending_question: { title: (p) => `Uma pergunta em ${p} está parada sem resposta`, origin: 'terminal', action: CENTRAL },
    engine_stalled: { title: (p) => `Um motor autônomo travou (${p})`, origin: 'motor', action: HEALTH },
    plan_backlog_pending: { title: () => 'Há backlog acionável e a frota está livre', origin: 'motor', action: { id: 'backlog', label: 'Ver o backlog', href: '/backlog' } },
    deep_implement_ready: { title: (p) => `Plano ampliado e validado em ${p}: pronto para implementar`, origin: 'motor', action: OPEN_PROJECT(null) },
    objective_reached: { title: (p) => `Objetivo do projeto ${p} foi atingido`, origin: 'juiz', action: OPEN_PROJECT(null) },
    runner_task: { title: (p) => `Tarefa delegada a um executor efêmero (${p})`, origin: 'master', action: HEALTH },
    evidence_needs_action: { title: (p) => `Evidência de ${p} pediu correção`, origin: 'dono', action: EVIDENCE },
    evidence_decision_requested: { title: (p) => `Evidência de ${p} espera a sua decisão`, origin: 'terminal', action: EVIDENCE },
    evidence_round_approved: { title: (p) => `Rodada de evidências de ${p} aprovada por inteiro`, origin: 'dono', action: EVIDENCE },
    evidence_queue_overdue: { title: (p) => `Evidências de ${p} esperam há tempo demais`, origin: 'sistema', action: EVIDENCE },
    communication_draft_approved: { title: (p) => `Mensagem ao cliente de ${p} aprovada`, origin: 'dono', action: APPROVALS },
    communication_draft_submitted: { title: (p) => `Nova mensagem ao cliente de ${p} espera revisão`, origin: 'terminal', action: APPROVALS },
    communication_queue_overdue: { title: (p) => `Mensagens ao cliente de ${p} esperam há tempo demais`, origin: 'sistema', action: APPROVALS },
    communication_draft_needs_correction: { title: (p) => `Mensagem ao cliente de ${p} voltou para correção`, origin: 'dono', action: APPROVALS },
    communication_draft_approval_expired: { title: (p) => `A aprovação da mensagem de ${p} venceu`, origin: 'sistema', action: APPROVALS },
    communication_draft_stale_by_conversation: { title: (p) => `A conversa com o cliente de ${p} mudou depois do rascunho`, origin: 'sistema', action: APPROVALS },
    terminal_settled: { title: (p) => `O terminal de ${p} concluiu o ciclo`, origin: 'terminal', action: OPEN_PROJECT(null) },
    terminal_idle_reclaimable: { title: (p) => `O terminal de ${p} está ocioso e pode ser liberado`, origin: 'motor', action: OPEN_PROJECT(null) },
    project_escalation_requested: { title: (p) => `${p} pediu a sua decisão`, origin: 'terminal', action: CENTRAL },
    project_cwd_orphaned: { title: (p) => `A pasta do projeto ${p} mudou: identidade a recuperar`, origin: 'sistema', action: CENTRAL },
    channel_owner_selection_required: { title: (p) => `Um canal de ${p} precisa de dono`, origin: 'sistema', action: { id: 'operations', label: 'Abrir operações', href: '/operations' } },
    project_learning_required: { title: (p) => `${p} saiu de um entrave: registrar o aprendizado`, origin: 'motor', action: OPEN_PROJECT(null) },
    session_lifecycle: { title: (p) => `Mudança no ciclo de vida de uma sessão (${p})`, origin: 'sistema', action: HEALTH },
    task_run_orphaned: { title: (p) => `Um trabalho de ${p} ficou sem executor`, origin: 'sistema', action: HEALTH },
    master_notice: { title: (_p, d) => (d ? humanizeTokens(firstSentence(d)) : 'Aviso ao dono'), origin: 'sistema', action: CENTRAL },
    user_request: { title: (_p, d) => (d ? `Seu pedido: ${humanizeTokens(firstSentence(d, 70))}` : 'Um pedido seu aguarda atendimento'), origin: 'dono', action: CENTRAL },
    topic_action_proposed: { title: (p) => `A Conversa propôs uma ação em ${p}`, origin: 'juiz', action: CONVERSA },
    manual: { title: (_p, d) => (d ? humanizeTokens(firstSentence(d)) : 'Evento registrado à mão'), origin: 'dono', action: CENTRAL },
});
export function describeMasterEvent(e, nowMs = Date.now()) {
    const project = e.subjectLabel || basename(e.subjectCwd) || null;
    const p = project ?? 'um projeto';
    const copy = KIND_COPY[e.kind];
    const rawTitle = copy ? copy.title(p, e.detail ?? null) : `${humanizeTokens(e.kind)} em ${p}`;
    const title = firstSentence(rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1));
    const ageMs = typeof e.lastAtMs === 'number' ? Math.max(0, nowMs - e.lastAtMs) : null;
    const deferredUntil = typeof e.nextEligibleAtMs === 'number' && e.nextEligibleAtMs > nowMs ? e.nextEligibleAtMs : null;
    const whenLabel = deferredUntil ? `${ageLabel(ageMs)} · volta ${new Date(deferredUntil).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ageLabel(ageMs);
    const urgency = e.priority === 'critical' ? 'late' : urgencyForAge(ageMs);
    const origin = copy?.origin ?? 'sistema';
    const primary = copy?.action ?? OPEN_PROJECT(project);
    const actions = [primary, { id: 'context', label: 'Ver contexto completo' }];
    const technical = [
        `evento: ${e.kind}${e.priority ? ` · prioridade ${e.priority}` : ''}${e.count && e.count > 1 ? ` · ${e.count}×` : ''}`,
        e.subjectCwd ? `cwd: ${e.subjectCwd}` : null,
        e.detail ?? null,
        e.decision ? `decisão: ${e.decision}${e.decisionText ? ` — ${e.decisionText}` : ''}` : null,
    ].filter((line) => !!line).join('\n');
    return { title, project, whenLabel, ageMs, urgency, origin, originLabel: DECISION_ORIGIN_LABEL[origin], actions, technical };
}
// ---------------------------------------------------------------------------------------------
// Rótulos de estado usados no /backlog ("em voo" e fila do Produto)
// ---------------------------------------------------------------------------------------------
const SESSION_STATUS_LABEL = Object.freeze({
    running: 'em execução', working: 'trabalhando', standby: 'em espera', starting: 'iniciando', exited: 'encerrado', paused: 'pausado', awaiting_shell: 'aguardando o shell',
});
export function sessionStatusLabel(status) {
    if (!status)
        return 'estado desconhecido';
    return SESSION_STATUS_LABEL[status] ?? humanizeTokens(status);
}
const WORK_ITEM_STATE_LABEL = Object.freeze({
    queued: 'na fila', acknowledged: 'recebido', in_progress: 'em execução', done: 'concluído', rejected: 'recusado', blocked: 'bloqueado', delivered: 'entregue', cancelled: 'cancelado',
});
export function workItemStateLabel(state) {
    if (!state)
        return 'estado desconhecido';
    return WORK_ITEM_STATE_LABEL[state] ?? humanizeTokens(state);
}
export function providerLabel(provider) {
    const v = (provider ?? '').replace(/^acp:/, '');
    return v === 'claude' ? 'Claude' : v === 'codex' ? 'Codex' : v === 'glm' ? 'GLM' : v === 'mimo' ? 'Mimo' : v === 'mimo pro' ? 'Mimo Pro' : v || 'provedor desconhecido';
}
//# sourceMappingURL=decision-copy.js.map